import { gql } from '@graphql/__generated__';
import type { TourModule } from '@modules/product-updates/tours/tourTypes';

export const MLS_TOUR_ANCHORS = {
  drawZone: 'mls-map-draw-zone',
  marketPulse: 'mls-map-market-pulse',
  buyers: 'mls-map-buyers',
  addListing: 'listing-mls-add',
  terms: 'listing-mls-terms',
  buyersSelect: 'mls-buyers-select',
} as const;

// Starts the first time a user opens the MLS map (`MlsMapView.tsx`), or from
// "Show me" on the release entry `content/releases/mls`.
export const mlsBrowseTour: TourModule = {
  steps: t => [
    {
      anchor: MLS_TOUR_ANCHORS.drawZone,
      title: t('Draw your area'),
      body: t(
        'Draw a zone on the map to only see the listings shared by other agencies there.',
      ),
      placement: 'left',
    },
    {
      anchor: MLS_TOUR_ANCHORS.marketPulse,
      title: t('Read the market'),
      body: t(
        'Market pulse shades each area by supply, demand, tension or price per m².',
      ),
      placement: 'bottom-end',
    },
    {
      anchor: MLS_TOUR_ANCHORS.buyers,
      title: t('Your buyers on the map'),
      body: t(
        "Show your buyers' searches on the map: listings that fit one of them are marked, so you can request access and send them the listing.",
      ),
      placement: 'bottom-end',
    },
  ],
  resolvePath: async () => '/mls',
};

const GET_TOUR_UNSHARED_LISTING = gql(/* GraphQL */ `
  query GetMlsShareTourListing($userId: uuid!) {
    unshared: lots(
      where: {
        broker_id: { _eq: $userId }
        shared_on_mls_at: { _is_null: true }
        status: {
          _in: [available, available_coming_soon, available_off_market]
        }
      }
      order_by: [{ created_at: desc }]
      limit: 1
    ) {
      id
    }
    any: lots(
      where: { broker_id: { _eq: $userId } }
      order_by: [{ created_at: desc }]
      limit: 1
    ) {
      id
    }
    visibleUnshared: lots(
      where: {
        shared_on_mls_at: { _is_null: true }
        status: {
          _in: [available, available_coming_soon, available_off_market]
        }
      }
      order_by: [{ created_at: desc }]
      limit: 1
    ) {
      id
    }
  }
`);

// Explains how to put a listing on the MLS, on the listing's Marketing ›
// MLS Network page (`ListingMlsSettings.tsx`). From "Show me" on
// `content/releases/mls-new-listing-emails`.
export const mlsShareListingTour: TourModule = {
  steps: t => [
    {
      anchor: MLS_TOUR_ANCHORS.addListing,
      title: t('Put this listing on the MLS'),
      body: t(
        'Switch it on to show the listing to agents in other agencies, who get an email if their office is nearby.',
      ),
      placement: 'bottom-start',
    },
    {
      anchor: MLS_TOUR_ANCHORS.terms,
      title: t('Set your terms'),
      body: t(
        'Choose the commission split and add a note for the agents who see your listing.',
      ),
      placement: 'top-start',
    },
  ],
  // The user's latest available listing not on the MLS yet, else their
  // latest listing.
  resolvePath: async ({ client, userId }) => {
    const { data } = await client.query({
      query: GET_TOUR_UNSHARED_LISTING,
      variables: { userId },
      fetchPolicy: 'network-only',
    });
    // Else an active listing they can see that is not on the MLS yet.
    const lotId =
      data?.unshared[0]?.id ?? data?.any[0]?.id ?? data?.visibleUnshared[0]?.id;
    return lotId == null ? null : `/listings/${lotId}/marketing/mls-network`;
  },
};

const GET_TOUR_MLS_BUYERS_LISTING = gql(/* GraphQL */ `
  query GetMlsBuyersEmailTourListing($userId: uuid!) {
    own: buyer_leads(
      where: { mls_share_id: { _is_null: false }, created_by: { _eq: $userId } }
      order_by: [{ updated_at: desc }]
      limit: 1
    ) {
      lot_id
    }
    any: buyer_leads(
      where: { mls_share_id: { _is_null: false } }
      order_by: [{ updated_at: desc }]
      limit: 1
    ) {
      lot_id
    }
  }
`);

// The Buyers tab of an MLS listing (`MlsBuyersLead.tsx`). Played from "Show
// me" on `content/releases/mls-bulk-email-listing-snippet`. Bulk actions only
// appear once buyers are selected, so the tour points at Select.
export const mlsBuyersEmailTour: TourModule = {
  steps: t => [
    {
      anchor: MLS_TOUR_ANCHORS.buyersSelect,
      title: t('Email your buyers about this listing'),
      body: t(
        'Select the buyers, then Bulk actions › Send email. In the editor, + › Insert snippet adds this listing with your branded brochure.',
      ),
      placement: 'bottom-end',
    },
  ],
  // The MLS listing the user last worked buyers on, else any with buyers. MLS
  // buyers have an `mls_share_id` (`is_mls` is an unrelated 2021 flag).
  resolvePath: async ({ client, userId }) => {
    const { data } = await client.query({
      query: GET_TOUR_MLS_BUYERS_LISTING,
      variables: { userId },
      fetchPolicy: 'network-only',
    });
    const lotId = data?.own[0]?.lot_id ?? data?.any[0]?.lot_id;
    return lotId == null ? null : `/mls/${lotId}/buyers`;
  },
};
